import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import {
  Pagination,
  PaginationItem,
  Stack,
  type StackProps,
  styled,
} from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { type ChangeEvent, type ReactElement, useState } from "react";

import { MessageCardsSkeleton } from "./MessageCardsSkeleton";

const slideInFromRight = keyframes`
  from {
    opacity: 0.125;
    transform: translateX(48px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInFromLeft = keyframes`
  from {
    opacity: 0.125;
    transform: translateX(-48px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const StyledPagination = styled(Pagination)(({ theme }) => ({
  "& .MuiPaginationItem-root": {
    minWidth: "24px",
    width: "24px",
    height: "24px",
    padding: 0,
    margin: "0 1px",
    borderRadius: "50%",
    backgroundColor: "transparent",
    fontSize: 0,
    "& .MuiTouchRipple-root": {
      display: "none",
    },
    "&:active": {
      backgroundColor: `${theme.palette.support[300]} !important`,
    },
    "&:hover": {
      backgroundColor: theme.palette.support[100],
      borderRadius: "50%",
    },
    "&.Mui-selected": {
      backgroundColor: "transparent",
      borderRadius: "50%",
    },
    "&.Mui-disabled": {
      backgroundColor: "transparent",
      opacity: "1 !important",

      "&::before": {
        backgroundColor: `${theme.palette.secondary[100]} !important`,
        opacity: "1 !important",
      },
      "&.Mui-selected": {
        backgroundColor: "transparent",
      },
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "8px",
      height: "8px",
      borderRadius: "50%",
      backgroundColor: theme.palette.secondary[200],
      transition: "background-color 0.2s",
    },
  },
  "& .Mui-selected": {
    "&::before": {
      backgroundColor: theme.palette.secondary[400],
    },
  },
  "& .MuiPaginationItem-previousNext": {
    color: theme.palette.secondary[200],
    "&.Mui-disabled": {
      color: theme.palette.secondary[100],
    },
    "&::before": {
      display: "none",
    },
    "&:hover": {
      backgroundColor: theme.palette.support[100],
      color: theme.palette.secondary[200],
    },
    "&:active": {
      backgroundColor: theme.palette.support[300],
    },
    "& .MuiSvgIcon-root": {
      fontSize: "24px",
      margin: 0,
    },
  },
}));

const AnimatedItemsStack = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "slideDirection",
})<{ slideDirection: "left" | "right" }>(({ slideDirection }) => ({
  animation: `${slideDirection === "right" ? slideInFromRight : slideInFromLeft} 500ms ease`,
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
}));

export interface HorizontalPaginatedListProps extends StackProps {
  items: Array<ReactElement>;
  /**
   * Number of items to display per page.
   * @default 3
   */
  itemsByPage?: number;
  paginationDisabled?: boolean;
  /**
   * Gap between items in the horizontal list.
   * @default "12px"
   */
  itemGap?: StackProps["gap"];
  /**
   * Whether the list is loading. Shows skeleton items when true.
   */
  isLoading?: boolean;
}

export function HorizontalPaginatedList({
  items,
  itemsByPage = 3,
  paginationDisabled = false,
  itemGap = "12px",
  isLoading = false,
  ...props
}: HorizontalPaginatedListProps) {
  const [page, setPage] = useState(1);
  const [slideDirection, setSlideDirection] = useState<"left" | "right">(
    "right",
  );

  const totalPages = Math.ceil(items.length / itemsByPage);
  const startIndex = (page - 1) * itemsByPage;
  const endIndex = startIndex + itemsByPage;

  const itemsToShow = items.slice(startIndex, endIndex);

  const handleChange = (_: ChangeEvent<unknown>, nextPage: number) => {
    if (nextPage === page) return;
    setSlideDirection(nextPage > page ? "right" : "left");
    setPage(nextPage);
  };

  if (isLoading) {
    return (
      <Stack gap={1} {...props}>
        <Stack
          flexDirection="row"
          justifyContent="center"
          flexWrap="wrap"
          gap={itemGap}
        >
          <MessageCardsSkeleton count={itemsByPage} variant="inline" />
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack gap={1} {...props}>
      <AnimatedItemsStack
        key={page}
        slideDirection={slideDirection}
        flexDirection="row"
        justifyContent="center"
        flexWrap="wrap"
        gap={itemGap}
      >
        {itemsToShow}
      </AnimatedItemsStack>
      <Stack alignItems="center">
        <StyledPagination
          count={totalPages}
          shape="rounded"
          onChange={handleChange}
          page={page}
          disabled={paginationDisabled}
          siblingCount={totalPages}
          boundaryCount={0}
          renderItem={(item) => (
            <PaginationItem
              slots={{ previous: ChevronLeft, next: ChevronRight }}
              {...item}
            />
          )}
        />
      </Stack>
    </Stack>
  );
}
